import { type SidebarBoxKey } from 'AdmissionsGuidance';
import { Card } from 'Card';
import { useEventLogger, useTargetBrandConfig } from 'FrontRoyalAngular';
import { memo, useCallback, useMemo } from 'react';
import { AppBrandConfigs } from 'AppBranding';
import { type ButtonConfig } from '../StudentDashboardSidebar.types';
import { LogoBoxHeader } from './LogoBoxHeader';
import { LogoBoxDescription } from './LogoBoxDescription';
import { LogoBoxButtons } from './LogoBoxButtons';
import { useMobileBoxContext } from '../MobileSidebar/MobileBoxContext';
import { useIsPrimaryMobileBox } from '../hooks/useIsPrimaryMobileBox';

type Props = {
    title: string;
    children: React.ReactNode;
    buttons?: ButtonConfig[];
    boxKey: SidebarBoxKey;
    color?: 'primary';
    // If true, the logo box will use the Quantic brand config, regardless of the current brand config.
    // The ExecEdEligibility box (aka PreApprovedForCohortBox) is always branded as Quantic.
    forceQuantic?: boolean;
};

function LogoBoxComponent({ title, children, buttons, boxKey, color, forceQuantic }: Props) {
    const _brandConfig = useTargetBrandConfig();
    const mobileBoxConfig = useMobileBoxContext();
    const isPrimaryMobileBox = useIsPrimaryMobileBox(boxKey);
    const brandConfig = useMemo(
        () => (forceQuantic ? AppBrandConfigs.quantic : _brandConfig),
        [forceQuantic, _brandConfig],
    );
    const EventLogger = useEventLogger();
    const logoBoxIcon = useMemo(
        () =>
            brandConfig.logoBoxIcon && (
                <img
                    className="h-full w-full object-contain"
                    src={brandConfig.logoBoxIcon}
                    alt={`${brandConfig.brandNameShort} logo`}
                />
            ),
        [brandConfig],
    );
    const handleButtonPress = useCallback(
        (button: ButtonConfig) => {
            EventLogger.log('student_dashboard_sidebar_box:clicked_button', {
                type: `${boxKey}:${button.id}`,
            });
            button.onPress?.();
        },
        [EventLogger, boxKey],
    );
    return (
        <LogoBoxPresentation
            logo={logoBoxIcon}
            title={title}
            buttons={buttons}
            boxKey={boxKey}
            color={color}
            handleButtonPress={handleButtonPress}
            isInMobileDrawer={!!mobileBoxConfig}
            isPrimaryMobileBox={isPrimaryMobileBox}
        >
            {children}
        </LogoBoxPresentation>
    );
}

export function LogoBoxPresentation({
    logo,
    title,
    buttons,
    children,
    color,
    handleButtonPress,
    isInMobileDrawer,
    isPrimaryMobileBox,
}: Props & {
    logo: React.ReactNode;
    handleButtonPress: (button: ButtonConfig) => void;
    isInMobileDrawer: boolean;
    isPrimaryMobileBox: boolean;
}) {
    return (
        <Card
            width="full"
            shadow={false}
            radius={isInMobileDrawer ? 8 : 5}
            startBorderColor={color}
            wrapperBorder={isPrimaryMobileBox ? 'allButStart' : 'default'}
        >
            <LogoBoxHeader title={title} logo={logo} />
            <LogoBoxDescription>{children}</LogoBoxDescription>
            {!!buttons?.length && <LogoBoxButtons buttons={buttons} handleButtonClick={handleButtonPress} />}
        </Card>
    );
}

export const LogoBox = memo(LogoBoxComponent);
